import { useMemo, useCallback } from 'react';
import {
  normalizeKeyForLookup,
  createLookupKey,
} from '../../../utils/stagedFiles';
import { getFileFieldId } from '../../../utils/fileHelpers';

/**
 * useFileAttachments - Custom hook to process and organize file metadata by field
 *
 * This hook manages three types of file attachments:
 * 1. Server files - Files from the centralized files table (via FileQueryService)
 * 2. Local files - File objects in form data (legacy upload path)
 * 3. Staged files - Eagerly uploaded files tracked in files table (STAGED status)
 *
 * Priority when displaying/validating: local > staged > server
 *
 * NOTE: After migration, all files use the centralized file module
 * (files.id = sub_form_files.id). The fileMetadata format is compatible
 * with both old and new systems.
 *
 * @param {Object} filledForm - The filled form object containing fileMetadata from server
 * @param {Array} subforms - Array of subform objects to filter relevant files
 * @param {Object} formDataBySubform - Current form data with newly uploaded File objects
 * @param {Object} draftStagedFiles - Staged files from draft/session, keyed by:
 *   - "formId-subformId-fieldId" (full key format)
 *   - "subformId-fieldId" (legacy key format)
 * @param {number|null} formId - Current form ID for file scoping (multi-form submissions)
 * @param {Array} deletedFileIds - IDs of server files marked for deletion
 * @returns {Object} - { serverFilesByField, localFilesByField, stagedFilesByField, getFilesForField, hasFilesForField }
 */
const useFileAttachments = (
  filledForm,
  subforms = [],
  formDataBySubform = {},
  draftStagedFiles = {},
  formId = null,
  deletedFileIds = []
) => {
  // Process existing server-side files
  const serverFilesByField = useMemo(() => {
    const metadata = {};

    if (!filledForm?.fileMetadata || !Array.isArray(filledForm.fileMetadata)) {
      return metadata;
    }

    const relevantSubformIds = new Set(subforms.map((s) => String(s.id)));
    const deletedIds = new Set(deletedFileIds || []);

    // Track files without proper subform_id for fallback handling
    const orphanedFiles = [];

    filledForm.fileMetadata.forEach((file) => {
      if (deletedIds.has(file.id)) return;

      // context is a JSON object with frontend metadata (e.g. { field_id, alt_text })
      // field_id is also available as a flat property for backward compat
      const fileFieldId = getFileFieldId(file);

      // Check if file's form_id matches (if both are set)
      const formIdMatches =
        !formId ||
        file.form_id === undefined ||
        file.form_id === null ||
        String(file.form_id) === String(formId);

      if (!formIdMatches) return;

      // Check if file has valid subform_id
      const hasValidSubformId =
        file.subform_id !== undefined &&
        file.subform_id !== null &&
        relevantSubformIds.has(String(file.subform_id));

      // Prepare the file object
      const fileName =
        file.original_name || file.name || file.file_path?.split('/').pop();
      const fileObject = {
        id: file.id,
        name: fileName,
        filename: fileName,
        path: file.file_path || file.provider_key,
        mimeType: file.mime_type,
        size: parseInt(file.file_size || file.size || 0, 10),
        isServerFile: true,
        status: file.status,
        sort_order: file.sort_order,
        context: file.context,
        // Keep original metadata for debugging
        _original_subform_id: file.subform_id,
        _original_field_id: fileFieldId,
      };

      if (hasValidSubformId && fileFieldId) {
        // Normal case: file has valid subform_id and field_id
        const key = createLookupKey(file.subform_id, fileFieldId);
        if (!metadata[key]) {
          metadata[key] = [];
        }
        metadata[key].push(fileObject);
      } else if (formIdMatches) {
        // Orphaned file: has matching form_id but missing subform_id or field_id
        // Store for fallback assignment to first file field
        orphanedFiles.push(fileObject);
      }
    });

    // Fallback: assign orphaned files to a special key that can be handled by components
    // This ensures files aren't completely lost even if metadata is incomplete
    if (orphanedFiles.length > 0) {
      metadata['__orphaned__'] = orphanedFiles;
      console.warn(
        `[useFileAttachments] ${orphanedFiles.length} file(s) without proper subform_id/field_id:`,
        orphanedFiles.map((f) => ({
          id: f.id,
          name: f.name,
          subform_id: f._original_subform_id,
          field_id: f._original_field_id,
        }))
      );
    }

    return metadata;
  }, [filledForm?.fileMetadata, subforms, formId, deletedFileIds]);

  // Process newly uploaded local files
  const localFilesByField = useMemo(() => {
    const metadata = {};

    Object.entries(formDataBySubform).forEach(([subformId, formData]) => {
      if (!formData || typeof formData !== 'object') return;

      Object.entries(formData).forEach(([fieldId, value]) => {
        // Check if value is a File or array of Files
        const files = Array.isArray(value) ? value : value ? [value] : [];
        const fileObjects = files.filter(
          (f) => f instanceof File || (f && f.name && f.type)
        );

        if (fileObjects.length > 0) {
          const key = createLookupKey(subformId, fieldId);
          metadata[key] = fileObjects.map((file, index) => ({
            id: `local-${subformId}-${fieldId}-${index}`,
            name: file.name,
            filename: file.name,
            mimeType: file.type,
            isLocalFile: true,
            file: file,
          }));
        }
      });
    });

    return metadata;
  }, [formDataBySubform]);

  /**
   * Process staged files from draft/current session.
   *
   * Staged files are eagerly uploaded files that haven't been submitted yet.
   * They are stored in the staged_files table and tracked by field key.
   *
   * This handles two key formats for backwards compatibility:
   * - Full key: "formId-subformId-fieldId" (current format, for multi-form isolation)
   * - Legacy key: "subformId-fieldId" (for older drafts without formId)
   *
   * Used when:
   * - Loading a draft for editing
   * - Tracking newly uploaded files during current session
   * - Validating required file fields
   */
  const stagedFilesByField = useMemo(() => {
    const metadata = {};

    if (!draftStagedFiles || typeof draftStagedFiles !== 'object') {
      return metadata;
    }

    Object.entries(draftStagedFiles).forEach(([key, files]) => {
      // Use centralized utility for key normalization
      const normalizedKey = normalizeKeyForLookup(key, formId);

      if (!normalizedKey) return;

      if (Array.isArray(files) && files.length > 0) {
        // Don't overwrite if we already have files for this key
        // This ensures formId-specific keys take precedence over legacy keys
        if (!metadata[normalizedKey]) {
          metadata[normalizedKey] = files.map((file) => ({
            id: file.id || file.stagedId,
            name: file.name,
            filename: file.name,
            mimeType: file.type,
            size: file.size,
            isStagedFile: true,
            stagedId: file.stagedId || file.id,
          }));
        }
      }
    });

    return metadata;
  }, [draftStagedFiles, formId]);

  /**
   * Get files for a specific field (combines server + local + staged files)
   *
   * Priority order (highest to lowest):
   * 1. Local files - File objects uploaded in current session (not yet staged)
   * 2. Staged files - Eagerly uploaded files in staged_files table
   * 3. Server files - Previously submitted files in sub_form_files table
   *
   * This priority ensures:
   * - New uploads override existing files
   * - Draft files show instead of old submitted files
   * - Server files are fallback when nothing else exists
   */
  const getFilesForField = useCallback(
    (subformId, fieldId) => {
      const key = createLookupKey(subformId, fieldId);
      const localFiles = localFilesByField[key] || [];
      const stagedFiles = stagedFilesByField[key] || [];
      const serverFiles = serverFilesByField[key] || [];

      // If there are local files, show them (new upload replaces old)
      if (localFiles.length > 0) {
        return localFiles;
      }

      // If there are staged files from draft, show them
      if (stagedFiles.length > 0) {
        return stagedFiles;
      }

      return serverFiles;
    },
    [localFilesByField, stagedFilesByField, serverFilesByField]
  );

  /**
   * Check if a field has existing files (server, local, or staged)
   */
  const hasFilesForField = useCallback(
    (subformId, fieldId) => {
      return getFilesForField(subformId, fieldId).length > 0;
    },
    [getFilesForField]
  );

  return {
    serverFilesByField,
    localFilesByField,
    stagedFilesByField,
    getFilesForField,
    hasFilesForField,
  };
};

export default useFileAttachments;
